/**
 * ProjectDashboard — full-screen dashboard overlay showing project metrics.
 *
 * Fetches data from GET /api/projects/:projectId/dashboard, then renders
 * six metric cards: Task Summary, Milestone Status, Team Workload,
 * Project Duration, Recent Activities, and KB Stats.
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../lib/api.js";
import type { ZohoTask, ZohoMilestone, ZohoUser } from "../types/zoho.js";
import {
	computeTaskSummary,
	computeTeamWorkload,
	computeProjectDuration,
	isOverdue,
	generateTasksCsv,
	generateMilestonesCsv,
} from "../utils/dashboardUtils.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProjectDashboardProps {
	projectId: string;
	onClose: () => void;
	apiUrl?: string;
}

interface DashboardData {
	tasks: ZohoTask[];
	milestones: ZohoMilestone[];
	recentActivities: Array<{
		id: string;
		content: string;
		activityBy: string;
		time: string;
		activityType: string;
	}>;
	users: ZohoUser[];
	kbStats: { totalChunks: number; lastIngestedAt: string | null };
}

type LoadingState = "loading" | "success" | "error";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Formats a date string (YYYY-MM-DD or ISO) to a human-readable form. */
function formatDate(dateStr: string | null): string {
	if (!dateStr) return "Not set";
	const d = new Date(dateStr);
	return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Formats an ISO timestamp to a relative time string. */
function formatRelativeTime(isoStr: string): string {
	const now = Date.now();
	const then = new Date(isoStr).getTime();
	if (isNaN(then)) return formatDate(isoStr);
	const diffMs = now - then;
	if (isNaN(diffMs) || diffMs < 0) return formatDate(isoStr);
	const diffMins = Math.floor(diffMs / 60_000);
	const diffHours = Math.floor(diffMs / 3_600_000);
	const diffDays = Math.floor(diffMs / 86_400_000);

	if (diffMins < 1) return "Just now";
	if (diffMins < 60) return `${diffMins} min ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays < 7) return `${diffDays}d ago`;
	return formatDate(isoStr);
}

/** Triggers a CSV file download in the browser. */
function downloadCsv(filename: string, csvContent: string): void {
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProjectDashboard({ projectId, onClose }: ProjectDashboardProps) {
	const [state, setState] = useState<LoadingState>("loading");
	const [data, setData] = useState<DashboardData | null>(null);
	const [exportOpen, setExportOpen] = useState(false);

	const fetchDashboard = useCallback(async () => {
		setState("loading");
		setData(null);
		try {
			const response = await apiGet(`/api/projects/${projectId}/dashboard`);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const json = (await response.json()) as DashboardData;
			setData(json);
			setState("success");
		} catch {
			setState("error");
		}
	}, [projectId]);

	useEffect(() => {
		fetchDashboard();
	}, [fetchDashboard]);

	// Close export dropdown on outside click
	useEffect(() => {
		if (!exportOpen) return;
		const handleClick = () => setExportOpen(false);
		document.addEventListener("click", handleClick);
		return () => document.removeEventListener("click", handleClick);
	}, [exportOpen]);

	// --- Loading state ---
	if (state === "loading") {
		return (
			<div className="dashboard-container">
				<div className="dashboard-loading" role="status" aria-label="Loading dashboard">
					<div className="loading-spinner" />
					<p>Loading dashboard...</p>
				</div>
			</div>
		);
	}

	// --- Error state ---
	if (state === "error" || !data) {
		return (
			<div className="dashboard-container">
				<div className="dashboard-error">
					<div className="dashboard-error__icon" aria-hidden="true">
						<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
							<circle cx="12" cy="12" r="10" />
							<line x1="12" y1="8" x2="12" y2="12" />
							<line x1="12" y1="16" x2="12.01" y2="16" />
						</svg>
					</div>
					<p className="dashboard-error__message">
						Unable to load dashboard data. Please check your connection and try again.
					</p>
					<button
						type="button"
						className="dashboard-error__retry"
						onClick={fetchDashboard}
					>
						Try again
					</button>
				</div>
			</div>
		);
	}

	// --- Success state: derive metrics ---
	const taskSummary = computeTaskSummary(data.tasks);
	const workload = computeTeamWorkload(data.tasks, data.users);
	const milestoneCompleted = data.milestones.filter((m) => m.completed).length;
	const milestoneTotal = data.milestones.length;
	const overdueCount = data.tasks.filter((t) => isOverdue(t)).length;

	// Compute project duration from milestones (since we don't have the project object)
	const projectForDuration = {
		id: projectId,
		name: "",
		status: "active" as const,
		description: "",
		createdDate: null,
		startDate: null as string | null,
		endDate: null as string | null,
		ownerName: "",
		taskCount: { open: 0, closed: 0 },
		milestoneCount: { open: 0, closed: 0 },
	};

	// Infer start/end from milestones if available
	if (data.milestones.length > 0) {
		let earliest: string | null = null;
		let latest: string | null = null;
		for (const m of data.milestones) {
			if (m.startDate && (!earliest || m.startDate < earliest)) earliest = m.startDate;
			if (m.endDate && (!latest || m.endDate > latest)) latest = m.endDate;
		}
		projectForDuration.startDate = earliest;
		projectForDuration.endDate = latest;
	}

	const durationMetrics = computeProjectDuration(projectForDuration, data.milestones);

	const handleExportTasks = () => {
		const csv = generateTasksCsv(data.tasks);
		downloadCsv("tasks.csv", csv);
	};

	const handleExportMilestones = () => {
		const csv = generateMilestonesCsv(data.milestones);
		downloadCsv("milestones.csv", csv);
	};

	return (
		<div className="dashboard-container">
			{/* Header */}
			<header className="dashboard-header">
				<div className="dashboard-title-group">
					<div className="dashboard-eyebrow">Project Overview</div>
					<h2 className="dashboard-title">
						Dashboard
						<span className="dashboard-badge">
							<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
								<rect x="3" y="3" width="7" height="7" />
								<rect x="14" y="3" width="7" height="7" />
								<rect x="14" y="14" width="7" height="7" />
								<rect x="3" y="14" width="7" height="7" />
							</svg>
							Live
						</span>
					</h2>
				</div>

				<div className="dashboard-header-actions">
					<button
						type="button"
						className="dashboard-btn dashboard-btn--ghost"
						onClick={() => window.print()}
						aria-label="Print dashboard"
					>
						<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<polyline points="6 9 6 2 18 2 18 9" />
							<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
							<rect x="6" y="14" width="12" height="8" />
						</svg>
						Print
					</button>

					<div className="dashboard-export-wrapper">
						<button
							type="button"
							className="dashboard-btn dashboard-btn--ghost"
							onClick={(e) => {
								e.stopPropagation();
								setExportOpen((o) => !o);
							}}
							aria-haspopup="true"
							aria-expanded={exportOpen}
						>
							<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
								<polyline points="7 10 12 15 17 10" />
								<line x1="12" y1="15" x2="12" y2="3" />
							</svg>
							Export
							<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
								<polyline points="6 9 12 15 18 9" />
							</svg>
						</button>
						{exportOpen && (
							<div className="dashboard-export-dropdown" role="menu">
								<button
									type="button"
									className="dashboard-export-item"
									role="menuitem"
									onClick={handleExportTasks}
								>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
										<rect x="3" y="3" width="18" height="18" rx="2" />
										<path d="M3 9h18M9 21V9" />
									</svg>
									Download Tasks CSV
								</button>
								<button
									type="button"
									className="dashboard-export-item"
									role="menuitem"
									onClick={handleExportMilestones}
								>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
										<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
										<polyline points="14 2 14 8 20 8" />
									</svg>
									Download Milestones CSV
								</button>
							</div>
						)}
					</div>

					<button
						type="button"
						className="dashboard-btn dashboard-btn--icon"
						onClick={onClose}
						aria-label="Close dashboard"
						title="Close dashboard"
					>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
							<line x1="18" y1="6" x2="6" y2="18" />
							<line x1="6" y1="6" x2="18" y2="18" />
						</svg>
					</button>
				</div>
			</header>

			{/* Dashboard body */}
			<div className="dashboard-body">
				<div className="dashboard-grid">

					{/* Card 1: Task Summary */}
					<div className="dashboard-card dashboard-card--enter" style={{ animationDelay: "0.05s" }}>
						<div className="dashboard-card__header">
							<div className="dashboard-card__title">
								<span className="dashboard-card__icon dashboard-card__icon--blue">
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
										<polyline points="9 11 12 14 22 4" />
										<path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
									</svg>
								</span>
								Task Summary
							</div>
							<span className="dashboard-card__meta">{taskSummary.total} total</span>
						</div>
						<div className="dashboard-card__body">
							{taskSummary.total === 0 ? (
								<div className="dashboard-card__empty">No tasks found for this project.</div>
							) : (
								<div className="task-summary-layout">
									<div className="donut-wrapper">
										<svg viewBox="0 0 130 130" className="donut-svg">
											<DonutChart
												data={[
													{ value: taskSummary.open, colour: "#1B6BC4" },
													{ value: taskSummary.inProgress, colour: "#F59E0B" },
													{ value: taskSummary.completed, colour: "#5DB329" },
													{ value: overdueCount, colour: "#E05252" },
												]}
												total={taskSummary.total}
											/>
										</svg>
										<div className="donut-centre">
											<div className="donut-centre-value">{taskSummary.total}</div>
											<div className="donut-centre-label">Total</div>
										</div>
									</div>
									<div className="task-stats">
										<TaskStatRow colour="#1B6BC4" label="Open" count={taskSummary.open} total={taskSummary.total} />
										<TaskStatRow colour="#F59E0B" label="In Progress" count={taskSummary.inProgress} total={taskSummary.total} />
										<TaskStatRow colour="#5DB329" label="Completed" count={taskSummary.completed} total={taskSummary.total} />
										<TaskStatRow colour="#E05252" label="Overdue" count={overdueCount} total={taskSummary.total} />
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Card 2: Milestone Status */}
					<div className="dashboard-card dashboard-card--enter" style={{ animationDelay: "0.1s" }}>
						<div className="dashboard-card__header">
							<div className="dashboard-card__title">
								<span className="dashboard-card__icon dashboard-card__icon--orange">
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
										<path d="M12 2L2 7l10 5 10-5-10-5z" />
										<path d="M2 17l10 5 10-5" />
										<path d="M2 12l10 5 10-5" />
									</svg>
								</span>
								Milestone Status
							</div>
							<span className="dashboard-card__meta">{milestoneTotal} total</span>
						</div>
						<div className="dashboard-card__body">
							{milestoneTotal === 0 ? (
								<div className="dashboard-card__empty">No milestones found for this project.</div>
							) : (
								<div className="milestone-list">
									{data.milestones.map((m) => (
										<div key={m.id} className="milestone-item">
											<div className={`milestone-status-dot ${m.completed ? "milestone-status-dot--done" : ""}`} />
											<div className="milestone-info">
												<div className="milestone-name">{m.name}</div>
												<div className="milestone-date">
													{m.completed
														? `Completed ${formatDate(m.completedDate)}`
														: m.endDate
															? `Due ${formatDate(m.endDate)}`
															: "No end date"
													}
												</div>
											</div>
											<div className="milestone-owner">{m.ownerName}</div>
										</div>
									))}
									<div className="milestone-summary">
										{milestoneCompleted} of {milestoneTotal} completed
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Card 3: Team Workload */}
					<div className="dashboard-card dashboard-card--enter" style={{ animationDelay: "0.15s" }}>
						<div className="dashboard-card__header">
							<div className="dashboard-card__title">
								<span className="dashboard-card__icon dashboard-card__icon--green">
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
										<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
										<circle cx="9" cy="7" r="4" />
										<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
										<path d="M16 3.13a4 4 0 0 1 0 7.75" />
									</svg>
								</span>
								Team Workload
							</div>
							<span className="dashboard-card__meta">Tasks by member</span>
						</div>
						<div className="dashboard-card__body">
							{workload.length === 0 ? (
								<div className="dashboard-card__empty">No assigned tasks to display.</div>
							) : (
								<div className="workload-list">
									{workload.map((w) => (
										<div key={w.userId} className="workload-row">
											<div className="workload-name">{w.userName}</div>
											<div className="workload-bar-track">
												{Object.entries(w.byStatus).map(([status, count]) => (
													<div
														key={status}
														className="workload-bar-segment"
														style={{
															width: `${(count / Math.max(1, ...workload.map((wl) => wl.total))) * 100}%`,
															backgroundColor: getStatusColour(status),
														}}
														title={`${status}: ${count}`}
													/>
												))}
											</div>
											<div className="workload-count">{w.total}</div>
										</div>
									))}
								</div>
							)}
						</div>
					</div>

					{/* Card 4: Project Duration */}
					<div className="dashboard-card dashboard-card--enter" style={{ animationDelay: "0.2s" }}>
						<div className="dashboard-card__header">
							<div className="dashboard-card__title">
								<span className="dashboard-card__icon dashboard-card__icon--red">
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
										<circle cx="12" cy="12" r="10" />
										<polyline points="12 6 12 12 16 14" />
									</svg>
								</span>
								Project Duration
							</div>
						</div>
						<div className="dashboard-card__body">
							<div className="duration-layout">
								<div className="duration-dates">
									<div className="duration-date-block">
										<div className="duration-date-label">Start</div>
										<div className="duration-date-value">{formatDate(durationMetrics.startDate)}</div>
									</div>
									<div className="duration-date-block">
										<div className="duration-date-label">Est. Completion</div>
										<div className="duration-date-value">{formatDate(durationMetrics.estimatedEnd)}</div>
									</div>
								</div>

								<div className="duration-stats">
									<div className="duration-stat">
										<div className="duration-stat-value">{durationMetrics.daysElapsed}</div>
										<div className="duration-stat-label">Days Elapsed</div>
									</div>
									<div className="duration-stat">
										<div className="duration-stat-value">
											{durationMetrics.daysRemaining != null ? durationMetrics.daysRemaining : "—"}
										</div>
										<div className="duration-stat-label">Days Remaining</div>
									</div>
								</div>

								{durationMetrics.percentElapsed != null && (
									<div className="duration-progress">
										<div className="duration-progress-labels">
											<span>Elapsed</span>
											<span>{durationMetrics.percentElapsed}%</span>
										</div>
										<div className="progress-bar-track">
											<div
												className="progress-bar-fill"
												style={{
													width: `${durationMetrics.percentElapsed}%`,
													background: "linear-gradient(90deg, #1B6BC4, #2278D4)",
												}}
											/>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Card 5: Recent Activities */}
					<div className="dashboard-card dashboard-card--enter" style={{ animationDelay: "0.25s" }}>
						<div className="dashboard-card__header">
							<div className="dashboard-card__title">
								<span className="dashboard-card__icon dashboard-card__icon--purple">
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
										<path d="M22 12h-4l-3 9L9 3l-3 9H2" />
									</svg>
								</span>
								Recent Activities
							</div>
							<span className="dashboard-card__meta">Latest updates</span>
						</div>
						<div className="dashboard-card__body">
							{data.recentActivities.length === 0 ? (
								<div className="dashboard-card__empty">No recent activity to display.</div>
							) : (
								<ul className="activity-list" aria-label="Recent project activity">
									{data.recentActivities.slice(0, 8).map((activity) => (
										<li key={activity.id} className="activity-item">
											<div className="activity-icon" style={{ background: "#E8F1FB" }}>
												<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1B6BC4" strokeWidth="2.5">
													<polyline points="9 11 12 14 22 4" />
													<path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
												</svg>
											</div>
											<div className="activity-content">
												<div className="activity-desc">{activity.content}</div>
												<div className="activity-meta">
													<span className="activity-person">{activity.activityBy}</span>
													<span className="activity-sep" aria-hidden="true">&middot;</span>
													<span className="activity-time">{formatRelativeTime(activity.time)}</span>
												</div>
											</div>
										</li>
									))}
								</ul>
							)}
						</div>
					</div>

					{/* Card 6: KB Stats */}
					<div className="dashboard-card dashboard-card--enter" style={{ animationDelay: "0.3s" }}>
						<div className="dashboard-card__header">
							<div className="dashboard-card__title">
								<span className="dashboard-card__icon dashboard-card__icon--teal">
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
										<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
										<polyline points="14 2 14 8 20 8" />
										<line x1="16" y1="13" x2="8" y2="13" />
										<line x1="16" y1="17" x2="8" y2="17" />
									</svg>
								</span>
								KB Stats
							</div>
						</div>
						<div className="dashboard-card__body">
							<div className="kb-stats-layout">
								<div className="kb-stat-block">
									<div className="kb-stat-value">{data.kbStats.totalChunks}</div>
									<div className="kb-stat-label">Indexed Chunks</div>
								</div>
								<div className="kb-stat-block">
									<div className="kb-stat-value">
										{data.kbStats.lastIngestedAt
											? formatRelativeTime(data.kbStats.lastIngestedAt)
											: "Never"
										}
									</div>
									<div className="kb-stat-label">Last Synced</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TaskStatRow({ colour, label, count, total }: { colour: string; label: string; count: number; total: number }) {
	const pct = total > 0 ? Math.round((count / total) * 100) : 0;
	return (
		<div className="task-stat-row">
			<span className="task-stat-dot" style={{ background: colour }} />
			<span className="task-stat-label">{label}</span>
			<span className="task-stat-count">{count}</span>
			<span className="task-stat-pct">{pct}%</span>
		</div>
	);
}

/** Simple SVG donut chart — no D3 dependency. */
function DonutChart({ data, total }: { data: Array<{ value: number; colour: string }>; total: number }) {
	const cx = 65;
	const cy = 65;
	const r = 50;
	const circumference = 2 * Math.PI * r;
	const safeTotal = total > 0 ? total : 1;

	let cumulativeOffset = 0;
	const arcs = data
		.filter((d) => d.value > 0)
		.map((d) => {
			const fraction = d.value / safeTotal;
			const dashLength = isNaN(fraction) ? 0 : fraction * circumference;
			const offset = cumulativeOffset;
			cumulativeOffset += dashLength;
			return { ...d, dashLength, offset };
		});

	return (
		<>
			{/* Background ring */}
			<circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef2f7" strokeWidth="14" />
			{/* Data arcs */}
			{arcs.map((arc, i) => (
				<circle
					key={i}
					cx={cx}
					cy={cy}
					r={r}
					fill="none"
					stroke={arc.colour}
					strokeWidth="14"
					strokeDasharray={`${arc.dashLength} ${circumference - arc.dashLength}`}
					strokeDashoffset={-arc.offset}
					strokeLinecap="butt"
					transform={`rotate(-90 ${cx} ${cy})`}
					style={{ transition: "stroke-dasharray 0.6s ease, stroke-dashoffset 0.6s ease" }}
				/>
			))}
		</>
	);
}

/** Returns a colour for common Zoho Projects statuses. */
function getStatusColour(status: string): string {
	if (status === "Done") return "#5DB329";
	if (status.startsWith("In Progress")) return "#F59E0B";
	if (status === "To Do" || status === "Open") return "#1B6BC4";
	if (status === "With Client") return "#8B5CF6";
	if (status === "Delayed") return "#E05252";
	if (status === "Not Required") return "#94A3B8";
	return "#64748B";
}
